import { Send, Instagram, ArrowUpRight, Wallet, Activity } from "lucide-react";
import { BRAND, RISK_DISCLOSURE } from "./data";
import { SectionLabel } from "./SocialProof";

export function MainCTA() {
  return (
    <section
      data-testid="main-cta-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Glow background */}
      <div className="absolute inset-0 hs-radial-glow opacity-90 pointer-events-none" />
      <div className="absolute inset-0 hs-grid-bg opacity-30 pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
        <div className="rounded-3xl border border-[#00FF66]/20 bg-gradient-to-b from-[#0a0a0a] to-[#050505] p-10 md:p-16 text-center relative overflow-hidden">
          {/* corner glow */}
          <div className="absolute -top-32 -right-32 h-64 w-64 bg-[#00FF66]/15 blur-3xl rounded-full pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 h-64 w-64 bg-[#00FF66]/10 blur-3xl rounded-full pointer-events-none" />

          <div className="relative">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00FF66]/30 bg-[#00FF66]/5"
              data-testid="cta-eyebrow"
            >
              <Activity className="h-3 w-3 text-[#00FF66]" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#00FF66]">
                Społeczność aktywna · codzienne analizy
              </span>
            </div>

            <h2
              data-testid="main-cta-headline"
              className="mt-7 font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
            >
              Poznaj rynek <span className="text-[#00FF66]">z innej perspektywy.</span>
            </h2>
            <p className="mt-5 text-neutral-400 text-lg max-w-2xl mx-auto leading-relaxed">
              Dołącz do społeczności HypeSignals_4u i poznaj nasze podejście do analizy
              kryptowalut, sygnałów tradingowych i zarządzania ryzykiem.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <a
                href={BRAND.telegram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="main-cta-telegram"
                className="inline-flex items-center justify-center gap-2 h-14 px-10 rounded-xl bg-[#00FF66] text-black font-bold text-base hover:bg-[#00CC52] transition-all hover:scale-[1.02] active:scale-[0.98] hs-button-glow"
              >
                <Send className="h-5 w-5" />
                Dołącz do Telegrama
                <ArrowUpRight className="h-4 w-4 opacity-70" />
              </a>
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="main-cta-instagram"
                className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-white/[0.04] border border-white/10 text-white font-medium text-base hover:bg-white/[0.08]"
              >
                <Instagram className="h-5 w-5" />
                Sprawdź Instagram
              </a>
              <a
                href={BRAND.okx}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="main-cta-okx"
                className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-white/[0.04] border border-white/10 text-white font-medium text-base hover:bg-white/[0.08]"
              >
                <Wallet className="h-5 w-5" />
                Giełda, z której korzystam
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-neutral-500">
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00FF66]" />
                Codzienne analizy rynku
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00FF66]" />
                Materiały edukacyjne
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00FF66]" />
                Zarządzanie ryzykiem
              </span>
            </div>

            <p className="mt-8 text-sm text-neutral-500">
              Masz pytania o społeczność? Napisz do mnie na{" "}
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-instagram-questions-link"
                className="text-[#00FF66] hover:underline font-medium"
              >
                Instagramie
              </a>{" "}
              — odpowiadam osobiście.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/[0.06] bg-[#040404]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <a
              href="#top"
              className="inline-flex items-center gap-2.5"
              data-testid="footer-brand"
            >
              <span className="h-9 w-9 rounded-md bg-[#00FF66] text-black font-display font-bold text-sm flex items-center justify-center">
                HS
              </span>
              <span className="font-display font-semibold tracking-tight text-white text-xl">
                HypeSignals<span className="text-[#00FF66]">_4u</span>
              </span>
            </a>
            <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
              Analiza rynku kryptowalut, sygnały tradingowe i edukacja w zamkniętej
              społeczności. Nacisk na strukturę, kontekst rynkowy i świadome zarządzanie
              ryzykiem.
            </p>

            <div className="mt-8 flex items-center gap-3">
              <a
                href={BRAND.telegram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-telegram"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#00FF66] text-black font-semibold text-sm hover:bg-[#00CC52] transition"
              >
                <Send className="h-4 w-4" /> Telegram
              </a>
              <a
                href={BRAND.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-white/[0.04] border border-white/10 text-white font-medium text-sm hover:bg-white/[0.08] transition"
              >
                <Instagram className="h-4 w-4" /> Instagram
              </a>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.22em] font-bold text-neutral-500">
              Nawigacja
            </h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a href="#oferta" className="text-neutral-300 hover:text-white">
                  Oferta
                </a>
              </li>
              <li>
                <a href="#opinie" className="text-neutral-300 hover:text-white">
                  Opinie
                </a>
              </li>
              <li>
                <a href="#analiza" className="text-neutral-300 hover:text-white">
                  Analiza
                </a>
              </li>
              <li>
                <a href="#proces" className="text-neutral-300 hover:text-white">
                  Jak to działa
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.22em] font-bold text-neutral-500">
              Społeczność
            </h4>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a
                  href={BRAND.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-white"
                >
                  Telegram
                </a>
              </li>
              <li>
                <a
                  href={BRAND.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-white"
                >
                  Instagram
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.22em] font-bold text-neutral-500">
              Info
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-neutral-400">
              <li>Analiza rynku</li>
              <li>Sygnały tradingowe</li>
              <li>Edukacja i ryzyko</li>
            </ul>
          </div>
        </div>

        {/* Full risk disclosure */}
        <div
          data-testid="footer-risk-disclosure"
          className="mt-14 pt-8 border-t border-white/[0.06]"
        >
          <SectionLabel>Informacja o ryzyku</SectionLabel>
          <p className="mt-4 text-xs md:text-sm text-neutral-500 leading-relaxed max-w-4xl">
            {RISK_DISCLOSURE}
          </p>
        </div>

        <div className="mt-10 pt-6 border-t border-white/[0.06] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-xs text-neutral-500">
            © {new Date().getFullYear()} HypeSignals_4u. Wszelkie prawa zastrzeżone.
          </p>
          <p className="text-xs text-neutral-600 md:text-right">
            HypeSignals_4u nie świadczy indywidualnego doradztwa inwestycyjnego.
          </p>
        </div>
      </div>
    </footer>
  );
}

export function StickyMobileCTA() {
  return (
    <div
      data-testid="sticky-mobile-cta"
      className="md:hidden fixed bottom-0 inset-x-0 z-30 px-4 pb-4 pt-3 bg-gradient-to-t from-[#050505] via-[#050505]/95 to-transparent pointer-events-none"
    >
      <a
        href={BRAND.telegram}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="sticky-telegram-cta"
        className="pointer-events-auto flex items-center justify-center gap-2 h-13 py-4 rounded-xl bg-[#00FF66] text-black font-bold text-base hs-button-glow"
      >
        <Send className="h-5 w-5" />
        Dołącz do Telegrama
      </a>
    </div>
  );
}
